import { cloneDeep, isEqual } from 'lodash';
import { memo, useCallback, useEffect, useMemo } from 'react';

import { ComponentProvider } from 'hb-builder/contexts';
import {
  useBuilderContext,
  useBuilderConfigContext,
  useBuilderComponentsContext,
} from 'hb-builder/hooks';
import { extractAndLoadFonts, INITIAL_FONTS } from 'hb-builder/utils/fonts';
import { getCustomFontName } from 'hb-react/backoffice/components/PageBuilder/utils/getCustomFontName';

export const Children = ({ item }) => {
  const children = item?.children?.filter(Boolean);
  const nbChildren = children?.length;

  return children.map((child, index) => (
    <Item
      item={child}
      itemIndex={index}
      key={child.id}
      nbParentChildren={nbChildren}
    />
  ));
};

const ItemMemoized = memo(
  ({
    canBeDoubleClicked,
    canBeDragged,
    canBeSelected,
    Component,
    isSelected,
    item,
    itemIndex,
    label,
    nbParentChildren,
    isInLastColumn,
    toolbar,
    toolbarMobile,
    type,
  }) => {
    const children = useMemo(
      () =>
        item?.children?.filter(Boolean)?.length > 0 ? (
          <Children item={item} />
        ) : (
          item.content
        ),
      [item],
    );

    return (
      <ComponentProvider
        canBeDoubleClicked={canBeDoubleClicked}
        canBeDragged={canBeDragged}
        canBeSelected={canBeSelected}
        isSelected={isSelected}
        item={item}
        itemIndex={itemIndex}
        label={label}
        nbParentChildren={nbParentChildren}
        isInLastColumn={isInLastColumn}
        toolbar={toolbar}
        toolbarMobile={toolbarMobile}
        type={type}
      >
        <Component.RenderBuilder>{children}</Component.RenderBuilder>
      </ComponentProvider>
    );
  },
  (prevProps, nextProps) => {
    const sameItem = isEqual(prevProps.item, nextProps.item);
    const sameItemContent = prevProps.item?.content === nextProps.item?.content;
    const sameItemAttributes = isEqual(
      prevProps.item?.attributes,
      nextProps.item?.attributes,
    );

    const sameItemIndex = prevProps.itemIndex === nextProps.itemIndex;
    const sameIsSelected = prevProps.isSelected === nextProps.isSelected;
    const sameIsInLastColumn =
      prevProps.isInLastColumn === nextProps.isInLastColumn;
    const sameNbParentChildren =
      prevProps.nbParentChildren === nextProps.nbParentChildren;

    return (
      sameItem &&
      sameItemContent &&
      sameItemAttributes &&
      sameItemIndex &&
      sameIsSelected &&
      sameIsInLastColumn &&
      sameNbParentChildren
    );
  },
);

export const Item = ({ item, itemIndex = 0, nbParentChildren }) => {
  const { selectedItem, fullDocument } = useBuilderContext();
  const config = useBuilderConfigContext();
  const { components } = useBuilderComponentsContext();
  const {
    hivebrite: { networkTheming },
  } = config;

  const Component = useMemo(() => components[item.type], [components, item]);

  useEffect(() => {
    const customFontName = getCustomFontName(networkTheming);

    extractAndLoadFonts(
      item,
      [
        ...INITIAL_FONTS,
        ...(config?.features?.text?.fonts || []).map(({ value }) => value),
      ],
      customFontName,
    );
  }, []);

  const isInLastColumn = useCallback(
    (document, item) => {
      const body = document?.children?.find((child) => child.type === 'Body');

      return body.children.find((bodyChild) => {
        if (bodyChild?.type === 'Section') {
          const lastColumn = bodyChild.children[bodyChild.children.length - 1];

          if (
            lastColumn.children.find((component) => component.id === item.id)
          ) {
            return true;
          }
        }

        return false;
      });
    },
    [fullDocument, item],
  );

  if (Component) {
    const {
      canBeDoubleClicked,
      canBeDragged,
      canBeSelected,
      componentLabel,
      componentType,
      toolbar,
      toolbarMobile,
    } = Component;
    const isSelected = canBeSelected && selectedItem?.id === item?.id;

    return (
      <ItemMemoized
        canBeDoubleClicked={canBeDoubleClicked}
        canBeDragged={canBeDragged}
        canBeSelected={canBeSelected}
        Component={Component}
        isSelected={isSelected}
        item={cloneDeep(item)} // Force new reference for each item
        itemIndex={itemIndex}
        label={componentLabel}
        nbParentChildren={nbParentChildren}
        isInLastColumn={isInLastColumn}
        toolbar={toolbar}
        toolbarMobile={toolbarMobile}
        type={componentType}
      />
    );
  }

  return null;
};

export default Item;
